/**
 * MeetupLayout — module-level chrome for all /meetup/* routes.
 *
 * Provides a sticky header (logo, nav: Home / My groups / Discover / Chat,
 * Sign-in / avatar) and a footer (About Meetup / Guidelines / Help / Terms).
 * Content is centered inside a max-w container with side padding so text
 * never hits the viewport edge.
 */
import { Outlet, Link, useLocation, useNavigate } from "react-router-dom";
import { useEffect, useState } from "react";
import axios from "axios";
import {
  Users, Compass, Calendar, MessageCircle, User as UserIcon,
  LogIn, Menu as MenuIcon, X,
} from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL || (typeof window !== "undefined" ? window.location.origin : "");
const api = axios.create({ baseURL: API, withCredentials: true });

const NAV_ITEMS = [
  { to: "/meetup",           label: "Discover",   Icon: Compass },
  { to: "/meetup?tab=mine",  label: "My groups",  Icon: Users },
  { to: "/meetup?tab=events",label: "My events",  Icon: Calendar },
  { to: "/hustlechat",       label: "Chat",       Icon: MessageCircle },
];

export default function MeetupLayout() {
  const [user, setUser] = useState(null);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const loc = useLocation();
  const nav = useNavigate();

  useEffect(() => {
    api.get("/api/auth/me")
      .then((r) => setUser(r.data))
      .catch(() => setUser(null));
  }, []);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  useEffect(() => { setMobileOpen(false); }, [loc.pathname]);

  const initials = (user?.full_name || user?.email || "?")
    .split(/\s|@/).slice(0, 2).map((s) => s[0] || "").join("").toUpperCase();

  return (
    <div className="hp-mu-layout">
      <header
        data-testid="meetup-header"
        className={`hp-mu-header ${scrolled ? "is-scrolled" : ""}`}
      >
        <div className="hp-mu-header__inner">
          <Link to="/meetup" className="hp-mu-brand" data-testid="meetup-brand">
            <span className="hp-mu-brand__mark">HM</span>
            <span className="hp-mu-brand__name">HustleMeetup</span>
          </Link>

          <nav className="hp-mu-nav" data-testid="meetup-nav">
            {NAV_ITEMS.map(({ to, label, Icon }) => {
              const active = loc.pathname === to.split("?")[0]
                          || (to === "/meetup" && loc.pathname === "/meetup");
              return (
                <Link key={to} to={to}
                      className={`hp-mu-nav__link ${active ? "is-active" : ""}`}
                      data-testid={`meetup-nav-${label.toLowerCase().replace(/\s+/g, "-")}`}>
                  <Icon size={14} strokeWidth={1.8}/>
                  <span>{label}</span>
                </Link>
              );
            })}
          </nav>

          <div className="hp-mu-header__actions">
            {user ? (
              <Link to="/account" className="hp-mu-avatar"
                    data-testid="meetup-header-avatar"
                    onClick={() => {
                      try {
                        import("@/components/AccountHeader").then((m) =>
                          m.rememberAccountReturn({
                            label: "HustleMeet",
                            href:  loc.pathname + loc.search,
                          }));
                      } catch (_e) { /* no-op */ }
                    }}
                    title={user.email}>
                {initials}
              </Link>
            ) : (
              <Link to={`/login?next=${encodeURIComponent(loc.pathname + loc.search)}`}
                    className="hp-mu-cta hp-mu-cta--sm"
                    data-testid="meetup-header-login">
                <LogIn size={14}/> Sign in
              </Link>
            )}
            <button type="button" onClick={() => setMobileOpen((v) => !v)}
                    className="hp-mu-mobile-toggle"
                    data-testid="meetup-mobile-toggle"
                    aria-label="Toggle menu">
              {mobileOpen ? <X size={18}/> : <MenuIcon size={18}/>}
            </button>
          </div>
        </div>

        {mobileOpen && (
          <div className="hp-mu-header__mobile" data-testid="meetup-mobile-nav">
            {NAV_ITEMS.map(({ to, label, Icon }) => (
              <Link key={to} to={to} className="hp-mu-nav__link"
                    onClick={() => setMobileOpen(false)}>
                <Icon size={14} strokeWidth={1.8}/> <span>{label}</span>
              </Link>
            ))}
            {!user && (
              <button onClick={() => { setMobileOpen(false); nav("/login"); }}
                      className="hp-mu-cta hp-mu-cta--sm"
                      data-testid="meetup-mobile-login">
                <LogIn size={14}/> Sign in
              </button>
            )}
          </div>
        )}
      </header>

      <main className="hp-mu-main">
        <Outlet />
      </main>

      <footer className="hp-mu-footer" data-testid="meetup-footer">
        <div className="hp-mu-footer__inner">
          <div className="hp-mu-footer__col">
            <p className="hp-mu-brand__name">HustleMeetup</p>
            <p className="hp-mu-footer__blurb">
              Real-world groups, real conversations. Built on the
              HustlePlaza platform — every group and event comes with a
              private HustleChat room baked in.
            </p>
          </div>
          <div className="hp-mu-footer__col">
            <p className="hp-mu-footer__label">Meetup</p>
            <ul>
              <li><Link to="/meetup">Discover groups</Link></li>
              <li><Link to="/meetup?tab=events">My events</Link></li>
              <li><Link to="/meetup/onboarding">Start a group</Link></li>
              <li><Link to="/hustlechat">Group chats</Link></li>
            </ul>
          </div>
          <div className="hp-mu-footer__col">
            <p className="hp-mu-footer__label">Community</p>
            <ul>
              <li><Link to="/about">About HustleMeetup</Link></li>
              <li><Link to="/community-guidelines">Community guidelines</Link></li>
              <li><Link to="/safety">Safety tips</Link></li>
              <li><Link to="/help">Help centre</Link></li>
            </ul>
          </div>
          <div className="hp-mu-footer__col">
            <p className="hp-mu-footer__label">Legal</p>
            <ul>
              <li><Link to="/privacy">Privacy</Link></li>
              <li><Link to="/terms">Terms</Link></li>
              <li><Link to="/cookies">Cookies</Link></li>
              <li><a href="mailto:hello@hustleplaza.com.au">Contact us</a></li>
            </ul>
          </div>
        </div>
        <div className="hp-mu-footer__bar">
          © {new Date().getFullYear()} HustlePlaza — HustleMeetup is a
          social groups module. All rights reserved.
        </div>
      </footer>
    </div>
  );
}

/* ── styles injected once ─────────────────────────────────────── */
if (typeof document !== "undefined" && !document.getElementById("hp-mu-layout-css")) {
  const css = `
  .hp-mu-layout { min-height:100vh; display:flex; flex-direction:column;
    background:#FDFBF7; color:#121614;
    font-family:"Inter","Helvetica Neue",Arial,sans-serif; }
  .hp-mu-header { position:sticky; top:0; z-index:40;
    background:rgba(253,251,247,0.85); backdrop-filter:saturate(150%) blur(20px);
    border-bottom:1px solid transparent; transition:border-color 0.3s, box-shadow 0.3s; }
  .hp-mu-header.is-scrolled { border-color:#E8E5DF;
    box-shadow:0 1px 3px rgba(18,22,20,0.04); }
  .hp-mu-header__inner { max-width:80rem; margin:0 auto; padding:0 16px;
    height:64px; display:flex; align-items:center; justify-content:space-between; gap:24px; }
  @media (min-width:640px) { .hp-mu-header__inner { padding:0 24px; } }
  @media (min-width:1024px) { .hp-mu-header__inner { padding:0 32px; height:80px; } }
  .hp-mu-brand { display:flex; align-items:center; gap:8px; text-decoration:none;
    color:#121614; transition:color 0.2s; }
  .hp-mu-brand:hover { color:#C05746; }
  .hp-mu-brand__mark { display:none; }
  .hp-mu-brand__name { font-family:"Cormorant Garamond",Georgia,serif;
    font-size:24px; font-weight:500; letter-spacing:-0.005em; margin:0; }
  @media (min-width:768px) { .hp-mu-brand__name { font-size:30px; } }
  .hp-mu-nav { display:none; align-items:center; gap:32px; }
  @media (min-width:768px) { .hp-mu-nav { display:flex; } }
  .hp-mu-nav__link { display:inline-flex; align-items:center; gap:6px;
    text-decoration:none; font-size:13px; letter-spacing:0.02em; color:#121614;
    transition:color 0.2s; padding:6px 0; }
  .hp-mu-nav__link:hover, .hp-mu-nav__link.is-active { color:#C05746; }
  .hp-mu-nav__link.is-active { font-weight:500; }
  .hp-mu-header__actions { display:flex; align-items:center; gap:8px; }
  .hp-mu-avatar { display:inline-flex; align-items:center; justify-content:center;
    width:40px; height:40px; border-radius:9999px; background:transparent;
    color:#121614; text-decoration:none; font-size:12px; letter-spacing:0.03em;
    font-weight:600; transition:background 0.2s; border:1px solid transparent; }
  .hp-mu-avatar:hover { background:#F3F0EA; }
  .hp-mu-cta { display:inline-flex; align-items:center; justify-content:center;
    gap:8px; padding:12px 20px; min-height:44px; box-sizing:border-box;
    background:#121614; color:#FDFBF7; text-decoration:none; font-size:13.5px;
    letter-spacing:0.04em; text-transform:uppercase; font-weight:500;
    border:none; cursor:pointer; font-family:inherit; border-radius:0; }
  .hp-mu-cta--sm { padding:8px 14px; min-height:36px; font-size:12px; letter-spacing:0.05em; }
  .hp-mu-cta--ghost { background:transparent; color:#121614; border:1px solid rgba(18,22,20,0.24); }
  .hp-mu-cta:hover { background:#2C3448; }
  .hp-mu-cta--ghost:hover { background:#F3F0EA; }
  .hp-mu-mobile-toggle { display:inline-flex; width:40px; height:40px; border-radius:9999px;
    background:transparent; border:none; cursor:pointer;
    align-items:center; justify-content:center; color:#121614; transition:background 0.2s; }
  .hp-mu-mobile-toggle:hover { background:#F3F0EA; }
  @media (min-width:768px) { .hp-mu-mobile-toggle { display:none; } }
  .hp-mu-header__mobile { display:flex; flex-direction:column; gap:2px; padding:12px 24px 16px;
    border-top:1px solid #E8E5DF; background:#FDFBF7; }
  @media (min-width:768px) { .hp-mu-header__mobile { display:none; } }
  .hp-mu-header__mobile .hp-mu-nav__link { padding:10px 0; font-size:14px; }
  .hp-mu-main { flex:1; max-width:80rem; width:100%; margin:0 auto; padding:32px 16px 48px; box-sizing:border-box; }
  @media (min-width:640px) { .hp-mu-main { padding:32px 24px 48px; } }
  @media (min-width:1024px) { .hp-mu-main { padding:40px 32px 64px; } }
  .hp-mu-footer { background:#121614; color:#FDFBF7; margin-top:64px; }
  .hp-mu-footer__inner { max-width:80rem; margin:0 auto; padding:56px 24px 32px;
    display:grid; grid-template-columns:2fr 1fr 1fr 1fr; gap:40px; }
  @media (max-width:880px) { .hp-mu-footer__inner { grid-template-columns:1fr 1fr; padding:40px 20px 24px; gap:32px; } }
  @media (max-width:520px) { .hp-mu-footer__inner { grid-template-columns:1fr; } }
  .hp-mu-footer .hp-mu-brand__name { color:#FDFBF7; margin:0 0 8px; }
  .hp-mu-footer__blurb { font-size:13.5px; line-height:1.6; opacity:0.7; margin:0;
    max-width:360px; }
  .hp-mu-footer__label { margin:0 0 12px; font-size:11px; letter-spacing:0.12em;
    text-transform:uppercase; color:#C29A4A; }
  .hp-mu-footer__col ul { list-style:none; padding:0; margin:0; display:flex;
    flex-direction:column; gap:8px; }
  .hp-mu-footer__col a { color:#FDFBF7; opacity:0.75; text-decoration:none;
    font-size:13.5px; transition:opacity 0.15s; }
  .hp-mu-footer__col a:hover { opacity:1; text-decoration:underline;
    text-underline-offset:3px; text-decoration-color:#C29A4A; }
  .hp-mu-footer__bar { max-width:80rem; margin:0 auto; padding:20px 24px;
    border-top:1px solid rgba(253,251,247,0.1); font-size:12px; opacity:0.55; }
  `;
  const style = document.createElement("style");
  style.id = "hp-mu-layout-css";
  style.textContent = css;
  document.head.appendChild(style);
}
